/**
 * ChaseListCard — red "please phone them" widget on the admin home.
 * Data source: GET /api/admin/invoice-chase-list.
 * Actions:  Mark contacted, View PDF, Call/Email quick-links.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';

const fmtMoney = (v) => '£' + Number(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Sort presets available above the chase table. Client-side only —
// the backend returns rows unsorted-by-preference and this component
// re-orders them so we don't need a round-trip to try a new sort.
const CHASE_SORTS = {
  longest_unpaid: {
    label: '⏱ Longest unpaid',
    hint: 'Oldest overdue first — chase the ones that have been ignored longest.',
    compare: (a, b) => (b.days_overdue || 0) - (a.days_overdue || 0),
  },
  biggest_amount: {
    label: '💰 Biggest amount',
    hint: 'Highest balance first — focus on the invoices that hurt cashflow most.',
    compare: (a, b) => (b.total || 0) - (a.total || 0),
  },
};

export function ChaseListCard({ onNavigateInvoices }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ rows: [], totals: {}, grace_period: {} });
  // Sort preference is remembered across sessions in localStorage so a
  // finance admin who always chases by amount doesn't have to re-select
  // every login. Defaults to longest_unpaid — matches the "oldest first"
  // instinct most bookkeepers already work with.
  const [sortKey, setSortKey] = useState(() => {
    try {
      const v = localStorage.getItem('kw:chase-sort');
      return v && CHASE_SORTS[v] ? v : 'longest_unpaid';
    } catch { return 'longest_unpaid'; }
  });
  const setSortAndPersist = (k) => {
    setSortKey(k);
    try { localStorage.setItem('kw:chase-sort', k); } catch { /* ignore */ }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.invoiceChaseList();
      setData(r);
    } catch (err) { toast.error(err?.message || 'Failed to load chase list'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const markContacted = async (row) => {
    const note = window.prompt(`Log the outcome of your call with ${row.parent_name || 'the parent'}: (optional)`, '');
    if (note === null) return;
    try {
      await api.markInvoiceContacted(row.invoice_id, note);
      toast.success('Logged — auto-escalations paused for now.');
      load();
    } catch (err) { toast.error(err?.message || 'Failed'); }
  };

  const downloadPdf = async (row) => {
    try {
      const getBlob = api.getInvoicePdfUrl(row.invoice_id);
      const blob = await getBlob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) { toast.error(err?.message || 'PDF failed'); }
  };

  const { rows: rawRows = [], totals = {}, grace_period = {} } = data;
  const sortedRows = useMemo(() => {
    const arr = [...rawRows];
    arr.sort(CHASE_SORTS[sortKey]?.compare || CHASE_SORTS.longest_unpaid.compare);
    return arr;
  }, [rawRows, sortKey]);
  const rows = sortedRows;
  const hasRows = rows.length > 0;
  const anyLocking = (totals.locked_parent_count || 0) > 0;

  return (
    <div className="card" data-testid="chase-list-card" style={{
      marginTop: '1.25rem',
      border: hasRows ? '2px solid hsl(0 78% 82%)' : '1px solid hsl(var(--border))',
      background: hasRows ? 'hsl(0 78% 98%)' : undefined,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ margin: 0, color: hasRows ? 'hsl(0 70% 32%)' : undefined }}>
            {hasRows ? '📞' : '✅'} Chase list
          </h3>
          <p style={{ margin: '0.15rem 0 0', color: 'hsl(var(--muted-foreground))', fontSize: '0.82rem' }}>
            {hasRows
              ? <>
                  <strong>{totals.count}</strong> overdue invoice{totals.count === 1 ? '' : 's'} · <strong>{fmtMoney(totals.amount)}</strong> total
                  {anyLocking && <> · <strong style={{ color: 'hsl(0 70% 35%)' }}>{totals.locked_parent_count} famil{totals.locked_parent_count === 1 ? 'y' : 'ies'} auto-locked</strong></>}
                  {grace_period.enabled
                    ? <> · Grace-period auto-lock at <strong>{grace_period.days} days</strong>.</>
                    : <> · Grace-period auto-lock is OFF — enable in Org Settings → Billing.</>}
                </>
              : 'No overdue invoices — nothing to chase right now.'}
          </p>
        </div>
        <button type="button" className="btn btn-ghost" onClick={load} data-testid="chase-refresh" style={{ fontSize: '0.78rem' }}>🔄 Refresh</button>
      </div>

      {loading ? (
        <div className="loading" data-testid="chase-loading">Loading…</div>
      ) : !hasRows ? null : (
        <>
          {/* Sort toggles — remembered per-browser via localStorage.
              Client-side sort so switching is instant with no refetch. */}
          <div
            data-testid="chase-sort-toggles"
            style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              flexWrap: 'wrap', marginBottom: '0.6rem',
            }}
          >
            <span style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>
              Sort by
            </span>
            {Object.entries(CHASE_SORTS).map(([k, opt]) => {
              const active = sortKey === k;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => setSortAndPersist(k)}
                  data-testid={`chase-sort-${k}`}
                  title={opt.hint}
                  style={{
                    fontSize: '0.75rem',
                    padding: '0.25rem 0.65rem',
                    borderRadius: 999,
                    border: `1px solid ${active ? 'hsl(0 70% 55%)' : 'hsl(0 20% 82%)'}`,
                    background: active ? 'hsl(0 70% 55%)' : 'white',
                    color: active ? 'white' : 'hsl(0 60% 28%)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'transform 150ms, background 150ms',
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
            <span style={{ fontSize: '0.7rem', color: 'hsl(var(--muted-foreground))', marginLeft: 'auto' }}>
              {CHASE_SORTS[sortKey]?.hint}
            </span>
          </div>
          <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }} data-testid="chase-table">
            <thead>
              <tr style={{ borderBottom: '2px solid hsl(0 78% 82%)', textAlign: 'left' }}>
                <th style={{ padding: '0.4rem 0.5rem' }}>Parent</th>
                <th style={{ padding: '0.4rem 0.5rem' }}>Invoice</th>
                <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Days overdue</th>
                <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Amount</th>
                <th style={{ padding: '0.4rem 0.5rem' }}>Contact</th>
                <th style={{ padding: '0.4rem 0.5rem' }}>Last logged</th>
                <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.invoice_id} data-testid={`chase-row-${r.invoice_id}`} style={{ borderTop: '1px solid hsl(0 20% 88%)' }}>
                  <td style={{ padding: '0.5rem' }}>
                    <div style={{ fontWeight: 600 }}>{r.parent_name || r.parent_user_id?.slice(0, 8)}</div>
                    {r.is_locking_bookings && (
                      <span style={{ display: 'inline-block', marginTop: 2, padding: '0.1rem 0.4rem', borderRadius: 999, background: 'hsl(0 78% 92%)', color: 'hsl(0 70% 30%)', fontSize: '0.68rem', fontWeight: 700 }}>
                        🔒 Bookings locked
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '0.5rem', fontFamily: 'ui-monospace, monospace', fontSize: '0.82rem' }}>{r.invoice_number || '(draft)'}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                    <span style={{
                      display: 'inline-block', padding: '0.15rem 0.55rem', borderRadius: 999,
                      background: r.days_overdue >= 14 ? 'hsl(0 78% 88%)' : r.days_overdue >= 3 ? 'hsl(38 95% 88%)' : 'hsl(200 60% 92%)',
                      color:      r.days_overdue >= 14 ? 'hsl(0 70% 26%)' : r.days_overdue >= 3 ? 'hsl(30 80% 30%)' : 'hsl(210 60% 30%)',
                      fontWeight: 700, fontSize: '0.75rem',
                    }}>
                      {r.days_overdue}d
                    </span>
                  </td>
                  <td style={{ padding: '0.5rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtMoney(r.total)}</td>
                  <td style={{ padding: '0.5rem', whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                    {r.parent_phone && <div><a href={`tel:${r.parent_phone}`} data-testid={`chase-call-${r.invoice_id}`} style={{ color: 'hsl(var(--primary))' }}>📞 {r.parent_phone}</a></div>}
                    {r.parent_email && <div><a href={`mailto:${r.parent_email}?subject=Overdue invoice ${r.invoice_number}`} data-testid={`chase-email-${r.invoice_id}`} style={{ color: 'hsl(var(--muted-foreground))' }}>✉ {r.parent_email}</a></div>}
                  </td>
                  <td style={{ padding: '0.5rem', fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))' }}>
                    {r.last_contacted_at
                      ? new Date(r.last_contacted_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
                      : '—'}
                  </td>
                  <td style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button type="button" onClick={() => markContacted(r)} className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '0.2rem 0.55rem' }} data-testid={`chase-mark-${r.invoice_id}`}>✓ Contacted</button>
                    {' '}
                    <button type="button" onClick={() => downloadPdf(r)} className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }} data-testid={`chase-pdf-${r.invoice_id}`}>📄</button>
                    {onNavigateInvoices && (
                      <>
                        {' '}
                        <button type="button" onClick={() => onNavigateInvoices(r)} className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }} data-testid={`chase-open-${r.invoice_id}`}>Open ↗</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
}

export default ChaseListCard;
